import { FC, useState, ChangeEvent, useLayoutEffect, useMemo, useCallback, MouseEvent } from 'react';
import { Button, DashboardCard, Loading, Modal, Table, Tag } from '../../../../components';
import {
	TGrupoConcessionaria,
	deleteAdminGroupDealer,
	getAllGroupDealers,
} from '../../../../services';
import { RegistrationPage } from '../../templates';
import {
	RegistrationEntities,
	TagActive,
	TagActiveLabel,
	useFilteredItems,
} from '../../../../utils';
import {
	ActionsContainer,
	Dashboard,
	TableWrapper,
	TagsContainer,
} from '../../Registration.styles';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { useToastStore } from '../../../../stores';
import { DashboardStatus } from '../../../../components/dashboard-card/DashboardCard';

const RegistrationGroups: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [groupToDelete, setGroupToDelete] = useState<number>();
	const [dealershipGroup, setDealershipGroups] = useState<TGrupoConcessionaria[]>([]);
	const [activeCard, setActiveCard] = useState<DashboardStatus | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const { filteredItems, filterLoading } = useFilteredItems({
		items: dealershipGroup,
		searchTerm,
		status: activeCard,
		filterFn: useCallback(
			(item: TGrupoConcessionaria, searchTerm: string, status: DashboardStatus | null) => {
				let matchesSearchTerm = true;
				if (searchTerm) {
					const matchesName = item.grupoConcessionaria
						?.toLowerCase()
						.includes(searchTerm.toLowerCase());

					matchesSearchTerm = matchesName;
				}

				switch (status) {
					case DashboardStatus.Active:
						return matchesSearchTerm && item.tagAtivo === TagActive.Active;

					case DashboardStatus.Inactive:
						return matchesSearchTerm && item.tagAtivo === TagActive.Inactive;

					default:
						return matchesSearchTerm;
				}
			},
			[]
		),
	});

	const activeGroup = useMemo(
		() => dealershipGroup?.filter((group) => group.tagAtivo === TagActive.Active),
		[dealershipGroup]
	);

	const inactiveGroup = useMemo(
		() => dealershipGroup?.filter((group) => group.tagAtivo === TagActive.Inactive),
		[dealershipGroup]
	);

	const tableColumns = useMemo(
		() => [
			{ id: 'description', label: 'Descrição' },
			{ id: 'status', label: 'Status' },
			{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const defineActiveLabel = useCallback((grupo: TGrupoConcessionaria) => {
		if (grupo.tagAtivo === TagActive.Active) {
			return TagActiveLabel.Active;
		}

		return TagActiveLabel.Inactive;
	}, []);

	const handleDeleteConfirm = useCallback((id: number) => {
		setOpenDeleteModal(true);
		setGroupToDelete(id);
	}, []);

	const rowMapper = useCallback(
		(item: TGrupoConcessionaria) => ({
			description: { value: item.grupoConcessionaria, rawValue: item.grupoConcessionaria },
			status: {
				value: (
					<TagsContainer>
						<Tag status={defineActiveLabel(item)} />
					</TagsContainer>
				),
				rawValue: defineActiveLabel(item).toString(),
			},
			actions: {
				value: (
					<ActionsContainer>
						<Tooltip title='Editar' placement='top' arrow>
							<Link to={`editar/${item.id}`}>
								<Button variant='icon' icon={<EditOutlined fontSize='inherit' />} />
							</Link>
						</Tooltip>
						<Tooltip title='Desativar' placement='top' arrow>
							<div>
								<Button
									variant='icon'
									icon={<DeleteOutlined fontSize='inherit' />}
									onClick={() => handleDeleteConfirm(item.id)}
								/>
							</div>
						</Tooltip>
					</ActionsContainer>
				),
			},
		}),
		[handleDeleteConfirm, defineActiveLabel]
	);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const handleDelete = async () => {
		try {
			const response = await deleteAdminGroupDealer({ id: groupToDelete!.toString() });

			if (!response.includes('sucesso')) {
				setToast({ message: response, variant: 'error' });
			} else {
				setToast({
					message: 'Grupo Concessionária removido com sucesso!',
					variant: 'success',
				});
				setDealershipGroups((prevGroups) =>
					prevGroups?.filter((group) => group.id !== groupToDelete)
				);
			}
		} catch {
			setToast({
				message: 'Erro ao remover o Grupo Concessionária!',
				variant: 'error',
			});
		} finally {
			setOpenDeleteModal(false);
		}
	};

	const handleFilterChange = (status: DashboardStatus) => {
		if (status === activeCard) {
			setActiveCard(null);
			return;
		}

		setActiveCard(status);
	};

	const handleClearSearch = (e: MouseEvent<HTMLButtonElement>) => {
		setSearchTerm('');
	}

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredItems?.map(rowMapper)} paginate />,
		[filteredItems, rowMapper, tableColumns]
	);

	useLayoutEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await getAllGroupDealers();
				const sorted = (response || []).sort((a, b) => {
					if (a.grupoConcessionaria! < b.grupoConcessionaria!) {
						return -1;
					}
					if (a.grupoConcessionaria! > b.grupoConcessionaria!) {
						return 1;
					}
					return 0;
				});
				setDealershipGroups(sorted || []);
			} catch {
				setToast({
					message: 'Erro ao buscar os grupos de concessionárias!',
					variant: 'error',
				});
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchData();
	}, [setToast]);

	if (loading) return <Loading fullScreen />;

	return (
		<>
			<Dashboard>
				<DashboardCard
					active={activeCard === DashboardStatus.Active}
					variant={DashboardStatus.Active}
					amount={activeGroup?.length ?? 0}
					onClick={() => handleFilterChange(DashboardStatus.Active)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Inactive}
					variant={DashboardStatus.Inactive}
					amount={inactiveGroup?.length ?? 0}
					onClick={() => handleFilterChange(DashboardStatus.Inactive)}
				/>
			</Dashboard>
			<RegistrationPage
				entity={RegistrationEntities.dealershipGroup}
				searchTerm={searchTerm}
				onChange={handleSearchChange}
				onClear={handleClearSearch}
			>
				<>
					<Modal
						open={openDeleteModal}
						onClose={() => setOpenDeleteModal(false)}
						title='Confirmação!'
						primaryButtonText='Sim'
						secondaryButtonText='Não'
						onPrimaryButtonClick={handleDelete}
						onSecondaryButtonClick={() => setOpenDeleteModal(false)}
					>
						Deseja realmente desativar?
					</Modal>
					<TableWrapper>{filterLoading ? <Loading /> : MemoizedTable}</TableWrapper>
				</>
			</RegistrationPage>
		</>
	);
};

export default RegistrationGroups;
